import { Node, mergeAttributes } from '@tiptap/core'
import { NodeViewWrapper, ReactNodeViewRenderer, type NodeViewProps } from '@tiptap/react'
import { MoveVertical } from 'lucide-react'
import { useRef } from 'react'

import { cn } from '@/lib/utils'

import type { SpacerAttrs } from '../types'

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        emailSpacer: {
            insertSpacer: (attrs?: Partial<SpacerAttrs>) => ReturnType
        }
    }
}

const DEFAULTS: SpacerAttrs = { height: 24 }
const PRESET_HEIGHTS = [8, 16, 24, 32, 48, 64]

/**
 * Vertical spacing block. Merchants drag the bottom handle to resize,
 * or use the inline stepper. Renders as a transparent <div style="height:XXpx">.
 */
export const SpacerExtension = Node.create({
    name: 'emailSpacer',
    group: 'block',
    atom: true,
    draggable: true,

    addAttributes() {
        return {
            height: {
                default: DEFAULTS.height,
                parseHTML: (el) => Number(el.getAttribute('data-height')) || DEFAULTS.height,
                renderHTML: (a) => ({ 'data-height': String(a.height) }),
            },
        }
    },

    parseHTML() {
        return [{ tag: 'div[data-type="email-spacer"]' }]
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, { 'data-type': 'email-spacer' }),
        ]
    },

    addCommands() {
        return {
            insertSpacer:
                (attrs) =>
                ({ chain }) =>
                    chain()
                        .focus()
                        .insertContent({
                            type: this.name,
                            attrs: { ...DEFAULTS, ...attrs },
                        })
                        .run(),
        }
    },

    addNodeView() {
        return ReactNodeViewRenderer(SpacerView)
    },
})

function SpacerView(props: NodeViewProps) {
    const { node, updateAttributes, selected } = props
    const height = (node.attrs as SpacerAttrs).height
    const startY = useRef<{ y: number; h: number } | null>(null)

    const onPointerDown = (e: React.PointerEvent) => {
        ;(e.target as Element).setPointerCapture(e.pointerId)
        startY.current = { y: e.clientY, h: height }
    }
    const onPointerMove = (e: React.PointerEvent) => {
        if (!startY.current) return
        const delta = e.clientY - startY.current.y
        const next = Math.max(4, Math.min(300, Math.round((startY.current.h + delta) / 4) * 4))
        if (next !== height) updateAttributes({ height: next })
    }
    const onPointerUp = () => {
        startY.current = null
    }

    return (
        <NodeViewWrapper
            className={cn(
                'group relative my-1 rounded-sm transition-colors',
                selected
                    ? 'bg-primary/5 outline outline-1 outline-primary/30'
                    : 'hover:bg-muted/30',
            )}
            style={{ height }}
        >
            <div
                className={cn(
                    'pointer-events-none absolute inset-0 flex items-center justify-center text-[10px] font-medium text-muted-foreground/70 opacity-0 transition-opacity',
                    (selected || undefined) && 'opacity-100',
                    'group-hover:opacity-100',
                )}
            >
                <span className="inline-flex items-center gap-1 rounded-full bg-background/80 px-2 py-0.5 backdrop-blur">
                    <MoveVertical className="h-3 w-3" />
                    {height}px
                </span>
            </div>

            {selected && (
                <div className="absolute -bottom-1.5 left-1/2 z-10 -translate-x-1/2">
                    <div className="flex items-center gap-1 rounded-full border bg-background px-1 py-0.5 shadow-md">
                        {PRESET_HEIGHTS.map((h) => (
                            <button
                                key={h}
                                type="button"
                                onClick={() => updateAttributes({ height: h })}
                                className={cn(
                                    'h-5 rounded-full px-1.5 text-[10px] transition-colors',
                                    height === h
                                        ? 'bg-primary text-primary-foreground'
                                        : 'text-muted-foreground hover:bg-muted',
                                )}
                            >
                                {h}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <div
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                className="absolute inset-x-0 bottom-0 h-1 cursor-row-resize opacity-0 group-hover:opacity-100"
            />
        </NodeViewWrapper>
    )
}
